import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

const PAGE_KEY = 'page';

export const SESSION_FILTERS = [
  'no_upcoming_sessions',
  'scheduled_sessions',
  'chem_call',
] as const;

export type SessionFilter = (typeof SESSION_FILTERS)[number];

const arrayParam = (params: URLSearchParams, key: string) =>
  params
    .getAll(key)
    .flatMap((value) => value.split(','))
    .map((value) => value.trim())
    .filter(Boolean);

// Ignore ids of tags deleted since they were put in the URL.
export const liveCohortFilter = (
  ids: string[],
  tags: { id: string }[],
  tagsLoaded: boolean,
) =>
  ids.filter(
    (id) => !tagsLoaded || id === 'none' || tags.some((tag) => tag.id === id),
  );

// Company member filters live in the URL; every change returns to page 1.
export const useMembersFilterParams = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const arrayFilter = useCallback(
    (key: string) => arrayParam(searchParams, key),
    [searchParams],
  );

  const setArrayFilter = useCallback(
    (key: string, selected: string[]) => {
      setSearchParams((currentParams) => {
        const nextParams = new URLSearchParams(currentParams);
        nextParams.delete(key);
        selected
          .filter(Boolean)
          .forEach((value) => nextParams.append(key, value));
        nextParams.set(PAGE_KEY, '1');
        return nextParams;
      });
    },
    [setSearchParams],
  );

  const sessionFilter = SESSION_FILTERS.filter(
    (filter) => searchParams.get(filter) === 'true',
  );

  const setSessionFilter = useCallback(
    (selected: string[]) => {
      setSearchParams((currentParams) => {
        const nextParams = new URLSearchParams(currentParams);
        SESSION_FILTERS.forEach((filter) => {
          if (selected.includes(filter)) {
            nextParams.set(filter, 'true');
          } else {
            nextParams.delete(filter);
          }
        });
        nextParams.set(PAGE_KEY, '1');
        return nextParams;
      });
    },
    [setSearchParams],
  );

  return { arrayFilter, sessionFilter, setArrayFilter, setSessionFilter };
};
